/**
 * **The sloop's funnel plume** — a coal-burner's smoke, built as flat
 * `BARON.PAL` polygons trailing astern and above her, and the same picture with
 * different numbers once she has been hit.
 *
 * The arithmetic, with no three.js in it, so every number below is checkable
 * without a GPU (`test/ship-smoke.test.ts`). `three/ship-smoke-ribbons.ts` is the
 * half that turns these answers into a mesh, and `flight/scene-ships.ts` is what
 * feeds it a ship. It is `ship-wake.ts`'s sibling in every respect the wake's own
 * header argues for, so what follows is only the four places it differs.
 *
 * ## Her own speed is the wind, and it costs nothing to say so
 *
 * The plume is `patrolPoseAt(t − age)` raised by `rise × age` — the wake's
 * construction with one term added. That single term does all the work the brief
 * asked for: smoke leaves the funnel and then **stays where it was made** while
 * the ship runs out from under it, so the plume lies astern along the course she
 * was steering, bends round her turns with her, shortens as she loses way and
 * stands up as a vertical column when she stops. Nothing here computes an
 * apparent wind; the apparent wind *is* the construction.
 *
 * The **ambient** wind is deliberately not in it. A mission's wind is a briefing
 * word (`still`/`light`/`strong`) with a direction the flight model uses and the
 * scene has never needed, and threading one in to lean a plume would be a new
 * dependency for a second-order effect. Flagged, not fixed.
 *
 * ## Two ribbons crossed, because the kill camera forbids a billboard
 *
 * A wake lies on the water and reads from any angle. A plume stands in the air,
 * and a single ribbon is invisible edge-on. The usual answer is to billboard it
 * at the camera — and this scene may not: `scene-draw.ts` advances the ships
 * **once a frame** and then the kill camera re-draws the same world geometry
 * through a second lens without re-emitting it, precisely because ships are not
 * billboards. A camera-facing plume would be wrong in the corner window on every
 * frame it appeared in.
 *
 * So each plume is **two ribbons crossed along their own centreline** — one with
 * its width laid abeam, one with its width laid square to that in the plume's own
 * vertical plane. Camera-independent, correct in the kill camera for free, and
 * four hundred and eighty indices a ship.
 *
 * ## The ramp is `SMOKE.BMP`'s own eleven entries, and it stops there
 *
 * `fire-palette.ts` recovered `SMOKE.BMP`'s palette by histogramming every
 * opaque pixel of the baked sheet: **exactly eleven entries, `BARON.PAL`
 * 64–74**, `#141414` to `#696969`. Those eleven are this ramp, whole and
 * unextended, so the plume is drawn in the colours the original drew smoke in
 * and in no others.
 *
 * The first cut walked past them, up the same unbroken grey family to 87
 * `#d7d7d7`, on the argument that thinning smoke against a bright sky goes pale.
 * That is true of smoke you can see through and **false of an opaque mesh**: the
 * plume is widest where that walk was palest, so the biggest thing in the frame
 * came out near-white and read as a sail. Measured on the harness's own stills
 * and reverted the same afternoon (`ship-sim.md` §5a.3).
 *
 * Opaque, with no alpha, for `ship-wake.ts`'s reason: a mesh that opts out of
 * the depth buffer under this renderer's reversed-Z draws zero pixels. Where the
 * wake could fade into the sea's own entry there is no single sky colour to fade
 * into, so the plume dissolves by **getting thinner** instead — it tapers to
 * nothing over its last nine-twentieths, which is what smoke does anyway and
 * costs no colour at all.
 *
 * ## Damage is this same plume with a slower walk and more of it
 *
 * `ship-damage.ts`'s `shipDistress` is 0 while she is unhurt and 1 once her
 * integrity is gone. It does three things and no more: the plume grows longer
 * and wider, and the walk down the ramp is *slowed*, so a hurt ship's smoke
 * stays black far further astern than a working boiler's does. That is the whole
 * of the damage-smoke feature — one system, one mesh, one parameter — which is
 * what `ship-sim.md` §5b means by the funnel system hosting it.
 */

import { SHIP_SPEED_MPS } from "@red-opus/engine";
import { SMOKE_TUNABLES } from "./smoke-trail.ts";

/**
 * Where the funnel top is in her body frame, metres — **+X starboard, +Y up,
 * bow −Z**, the frame `ship-class.ts` states.
 *
 * Read off `ship-design.md` §3.2's funnel row rather than off the model: the
 * funnel is welded into `HULL` and has no rig node of its own to interrogate,
 * unlike every mounting and both searchlights. That row gives 4.70 → **11.30**
 * for its run, raked **0.55 aft**, standing on a boiler casing that spans
 * −3.00…+3.20 in the generator's own bow-toward-+Y frame — which is +3.00…−3.20
 * here, centred at −0.10. Base to top plus the rake puts the lip at +0.45.
 * `ship-smoke.test.ts` asserts it is inside her beam and above her bridge, which
 * is what a stated offset can be held to.
 */
export const FUNNEL_TOP_M = { x: 0, y: 11.3, z: 0.45 } as const;

/**
 * Seconds a plume stays in the air. **OURS.**
 *
 * A time rather than a length, for `WAKE_LIFE_S`'s reason: the ship then runs
 * out from under exactly as much of it as she is going, so a plume shortens with
 * her speed and vanishes into a standing column when she stops, with no second
 * constant anywhere. At `SHIP_SPEED_MPS` it works out at **175 m astern and 60 m
 * up** — a little over two hull lengths back, which is a coal-burner in a period
 * photograph.
 *
 * **Thirty was the first attempt and it was too much** (2026-09-01, measured on
 * the harness's own stills): at three hull lengths and 90 m up the plume was the
 * largest object in every frame she appeared in, and an opaque mesh that big
 * stops reading as smoke and starts reading as a sail. The other half of that
 * fix is the ramp below.
 */
export const SMOKE_LIFE_S = 20;

/** How far astern the plume reaches at her service speed — the claim above, as a number a test can measure. */
export const SMOKE_ASTERN_AT_SERVICE_M = SHIP_SPEED_MPS * SMOKE_LIFE_S;

/**
 * How fast the plume rises, m/s. **BORROWED**, from `smoke-trail.ts`'s
 * `puffRiseMps` — this port's one number for how fast a puff of *this same art*
 * climbs, still CALIBRATION-PENDING there and no less so here. Borrowing a
 * pending number beats inventing a second one that would have to be retuned
 * twice.
 */
export const SMOKE_RISE_MPS = SMOKE_TUNABLES.puffRiseMps;

/** Stations along each ribbon, minus one. 20 puts them 13 m apart at service speed. */
export const SMOKE_SEGMENTS = 20;
export const SMOKE_STATIONS = SMOKE_SEGMENTS + 1;
/** Lanes across a ribbon: one edge, the centre, the other edge. */
export const SMOKE_LANES = 3;
/** The abeam ribbon and the one crossed square to it. See the module note. */
export const SMOKE_RIBBONS = 2;

export const SMOKE_VERTEX_COUNT = SMOKE_RIBBONS * SMOKE_STATIONS * SMOKE_LANES;
export const SMOKE_INDEX_COUNT = SMOKE_RIBBONS * SMOKE_SEGMENTS * (SMOKE_LANES - 1) * 6;

/** Half-width of the plume where it leaves the lip, metres — the funnel's own 1.60 m major axis. */
const MOUTH_HALF_WIDTH_M = 0.8;

/**
 * How fast the plume spreads, radians of half-angle. **OURS — two and a half
 * degrees**, which puts it about one beam across at the far end of its own
 * length. Six was the first attempt and it made a wedge sixty metres across at
 * the tail; the picture that fixed it is the one a period photograph has, where
 * a funnel's smoke stays a rope rather than opening into a fan.
 */
const SPREAD_RAD = (2.5 * Math.PI) / 180;

/** How much wider a plume gets when she is burning rather than steaming. OURS. */
const DISTRESS_SPREAD = 2.5;

/** How much longer, likewise: a ship on fire makes smoke long after her funnel would have. */
const DISTRESS_LIFE = 1.6;

/**
 * The last nine-twentieths taper to nothing.
 *
 * More than twice the wake's fraction, because the wake had somewhere to go —
 * its ramp ends on `WATER_PALETTE_INDEX` itself and dissolves into the sea. A
 * plume has no one colour of sky to dissolve into and its own ramp deliberately
 * never leaves the dark greys, so **the taper is the whole of how it ends**, and
 * it is given nearly half the plume's length to do it in so that no bar of grey
 * is ever drawn across the sky.
 */
const TAPER_FRACTION = 0.45;

/**
 * The plume ramp: **`SMOKE.BMP`'s own eleven entries and nothing else** —
 * `BARON.PAL` 64 to 74, `#141414` to `#696969`, sootiest first.
 *
 * `fire-palette.ts` recovered that run by histogramming every opaque pixel of
 * the baked sheet, and it is exactly these eleven contiguous entries. This list
 * is that census, unextended, which is why the claim that the plume is a picture
 * a 1990 game could have drawn is literal here rather than argued.
 *
 * Eleven rungs where the foam ramp has thirteen, and that is a smaller ladder
 * over a much smaller range of luminance — 20 to 105 against the foam's 48 to
 * 215 — which is the point: **smoke does not go pale, it goes away.** See the
 * module note for the cut that walked past 74 and the still that sent it back.
 */
export const PLUME_RAMP_INDICES: readonly number[] = [64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74];

/**
 * Those indices' colours, in the same order.
 * `test/ship-smoke.test.ts` re-derives them from
 * `data/baked/palettes/BARON.PAL.json` and fails if one is not the real byte, so
 * this list cannot drift from the file — `ship-wake.ts`'s idiom, and
 * `sky-run.ts`'s before it.
 */
export const PLUME_RAMP_COLOURS: readonly (readonly [number, number, number])[] = [
  [20, 20, 20],
  [28, 28, 28],
  [36, 36, 36],
  [44, 44, 44],
  [52, 52, 52],
  [60, 60, 60],
  [73, 73, 73],
  [81, 81, 81],
  [89, 89, 89],
  [97, 97, 97],
  [105, 105, 105],
];

/** Rungs an edge vertex sits above its own centre, so a ribbon has soft sides without any alpha. */
const EDGE_RUNG_STEP = 2;

/** A palette entry as three 0..1 floats. */
export type PlumeRamp = Float32Array;

/** Turns a list of 0..255 triples into the 0..1 rungs the mesh writes. */
export function plumeRampOf(colours: readonly (readonly number[])[]): PlumeRamp {
  const ramp = new Float32Array(colours.length * 3);
  colours.forEach((rgb, rung) => {
    ramp[rung * 3] = (rgb[0] ?? 0) / 255;
    ramp[rung * 3 + 1] = (rgb[1] ?? 0) / 255;
    ramp[rung * 3 + 2] = (rgb[2] ?? 0) / 255;
  });
  return ramp;
}

export const PLUME_RAMP: PlumeRamp = plumeRampOf(PLUME_RAMP_COLOURS);

/** Where a ship was and which way she was pointing at some past instant — `ship-wake.ts`'s own `WakePose`. */
export interface SmokePose {
  readonly x: number;
  readonly z: number;
  readonly headingRad: number;
}

/** Her pose `ageS` seconds ago. `scene-ships.ts` builds one out of `shipMotionAt`. */
export type SmokePoseAt = (ageS: number) => SmokePose;

/** Vertex slot for one lane of one station of one ribbon. */
export function smokeVertexIndex(ribbon: number, station: number, lane: number): number {
  return (ribbon * SMOKE_STATIONS + station) * SMOKE_LANES + lane;
}

/** The triangle list, which never changes — two quads across each segment of each ribbon. */
export function smokeIndices(): Uint16Array {
  const indices = new Uint16Array(SMOKE_INDEX_COUNT);
  let at = 0;
  for (let ribbon = 0; ribbon < SMOKE_RIBBONS; ribbon += 1) {
    for (let station = 0; station < SMOKE_SEGMENTS; station += 1) {
      for (let lane = 0; lane < SMOKE_LANES - 1; lane += 1) {
        const a = smokeVertexIndex(ribbon, station, lane);
        const b = smokeVertexIndex(ribbon, station, lane + 1);
        const c = smokeVertexIndex(ribbon, station + 1, lane + 1);
        const d = smokeVertexIndex(ribbon, station + 1, lane);
        indices.set([a, b, c, a, c, d], at);
        at += 6;
      }
    }
  }
  return indices;
}

/**
 * How far down the ramp the plume has walked at `t` along its length.
 *
 * `t ** (1 + 2·distress)` — linear for a working boiler, cubic for a burning
 * ship. The exponent is the *whole* of the difference between funnel smoke and
 * damage smoke's colour: a hurt ship's plume is still soot at the funnel and
 * still pale at the tail, it just takes three times as far to get there.
 */
export function plumeRung(t: number, distress: number): number {
  const walked = Math.pow(Math.min(1, Math.max(0, t)), 1 + 2 * distress);
  return Math.min(PLUME_RAMP_INDICES.length - 1, Math.round(walked * (PLUME_RAMP_INDICES.length - 1)));
}

/**
 * The colour of every vertex of one plume.
 *
 * Written every frame rather than once at construction, which is the one place
 * this pool costs more than the wake's: the walk depends on `distress`, and
 * distress changes as she is shot. It is 126 vertices a ship.
 */
export function writeSmokeColours(
  out: Float32Array,
  baseVertex: number,
  ramp: PlumeRamp,
  distress: number,
): void {
  const top = PLUME_RAMP_INDICES.length - 1;
  for (let ribbon = 0; ribbon < SMOKE_RIBBONS; ribbon += 1) {
    for (let station = 0; station < SMOKE_STATIONS; station += 1) {
      const centre = plumeRung(station / SMOKE_SEGMENTS, distress);
      for (let lane = 0; lane < SMOKE_LANES; lane += 1) {
        // An edge is *paler* than its own centre, where a wake's edge is darker
        // than its crest: broken water is white against a dark sea and smoke is
        // dark against a bright sky, so "softer" is the opposite direction on
        // the same kind of ramp.
        const rung = lane === 1 ? centre : Math.min(top, centre + EDGE_RUNG_STEP);
        const at = (baseVertex + smokeVertexIndex(ribbon, station, lane)) * 3;
        out[at] = ramp[rung * 3] ?? 0;
        out[at + 1] = ramp[rung * 3 + 1] ?? 0;
        out[at + 2] = ramp[rung * 3 + 2] ?? 0;
      }
    }
  }
}

/** Half-width of the plume at `t`, before the taper. */
function plumeHalfWidthM(t: number, lengthM: number, distress: number): number {
  const raw = (MOUTH_HALF_WIDTH_M + t * lengthM * Math.tan(SPREAD_RAD)) * (1 + DISTRESS_SPREAD * distress);
  if (t <= 1 - TAPER_FRACTION) return raw;
  return raw * ((1 - t) / TAPER_FRACTION);
}

/** How long the plume lives at this level of distress — longer once she is alight. */
export function plumeLifeS(distress: number): number {
  return SMOKE_LIFE_S * (1 + (DISTRESS_LIFE - 1) * distress);
}

/**
 * The plume's centreline, station by station, into a scratch buffer.
 *
 * Module-level and reused, because a `Float32Array` per ship per frame is 180
 * allocations a second for a picture that otherwise costs nothing — the same
 * bargain `ShipWakeRibbons.push` makes by writing straight into the pool. It
 * makes `writeSmokePositions` non-reentrant, which is stated rather than
 * defended: one caller, one thread, one plume at a time.
 */
const centres = new Float32Array(SMOKE_STATIONS * 3);

/**
 * Writes one plume's vertex positions.
 *
 * Unlike the wake this never declines: a ship stopped dead still has a fire in
 * her, and a ship going down smokes hardest of all. The plume simply becomes a
 * vertical column, which is what `poseAt` returning the same point for every age
 * produces on its own.
 */
export function writeSmokePositions(
  out: Float32Array,
  baseVertex: number,
  poseAt: SmokePoseAt,
  waterY: number,
  distress: number,
): void {
  const lifeS = plumeLifeS(distress);
  for (let station = 0; station < SMOKE_STATIONS; station += 1) {
    const t = station / SMOKE_SEGMENTS;
    const ageS = t * lifeS;
    const pose = poseAt(ageS);
    // Her body frame at that instant: bow toward −z of her head, starboard a
    // quarter-turn round from it (`ship-wake.ts` uses the same two lines).
    const forwardX = Math.sin(pose.headingRad);
    const forwardZ = -Math.cos(pose.headingRad);
    const at = station * 3;
    centres[at] = pose.x + -forwardZ * FUNNEL_TOP_M.x + forwardX * -FUNNEL_TOP_M.z;
    centres[at + 1] = waterY + FUNNEL_TOP_M.y + SMOKE_RISE_MPS * ageS;
    centres[at + 2] = pose.z + forwardX * FUNNEL_TOP_M.x + forwardZ * -FUNNEL_TOP_M.z;
  }
  // A second pass, because a ribbon's width is laid square to its own tangent
  // and a tangent needs the station on either side. Differencing the centreline
  // is exact where an analytic tangent would have to assume a constant speed
  // she does not have while she is sinking.
  const lengthM = plumeLengthM(centres);
  for (let station = 0; station < SMOKE_STATIONS; station += 1) {
    writeStationLanes(out, baseVertex, station, lengthM, distress);
  }
}

/** Straight-line reach of the centreline, for the spread — the plume's own length rather than a speed times a time. */
function plumeLengthM(line: Float32Array): number {
  const last = (SMOKE_STATIONS - 1) * 3;
  return Math.hypot(
    (line[last] ?? 0) - (line[0] ?? 0),
    (line[last + 1] ?? 0) - (line[1] ?? 0),
    (line[last + 2] ?? 0) - (line[2] ?? 0),
  );
}

/** The six vertices of one station: three on the abeam ribbon, three on the one crossed with it. */
function writeStationLanes(
  out: Float32Array,
  baseVertex: number,
  station: number,
  lengthM: number,
  distress: number,
): void {
  const at = station * 3;
  const previous = Math.max(0, station - 1) * 3;
  const next = Math.min(SMOKE_STATIONS - 1, station + 1) * 3;
  let tx = (centres[next] ?? 0) - (centres[previous] ?? 0);
  let ty = (centres[next + 1] ?? 0) - (centres[previous + 1] ?? 0);
  let tz = (centres[next + 2] ?? 0) - (centres[previous + 2] ?? 0);
  const tangent = Math.hypot(tx, ty, tz) || 1;
  tx /= tangent;
  ty /= tangent;
  tz /= tangent;
  // The abeam direction: horizontal and square to the tangent's own ground
  // track. A plume standing straight up has no ground track, so it falls back to
  // the world x axis — any horizontal direction is as good as any other for a
  // column, and picking one keeps the mesh from collapsing.
  const groundLength = Math.hypot(tx, tz);
  const abeamX = groundLength > 1e-6 ? -tz / groundLength : 1;
  const abeamZ = groundLength > 1e-6 ? tx / groundLength : 0;
  // The second ribbon's width is the cross product of the two above: square to
  // the tangent and to the abeam, which puts it in the plume's own vertical
  // plane.
  const crossX = ty * abeamZ;
  const crossY = tz * abeamX - tx * abeamZ;
  const crossZ = -ty * abeamX;
  const crossLength = Math.hypot(crossX, crossY, crossZ) || 1;
  const half = plumeHalfWidthM(station / SMOKE_SEGMENTS, lengthM, distress);
  for (let lane = 0; lane < SMOKE_LANES; lane += 1) {
    const offset = (lane - 1) * half;
    const abeamAt = (baseVertex + smokeVertexIndex(0, station, lane)) * 3;
    out[abeamAt] = (centres[at] ?? 0) + abeamX * offset;
    out[abeamAt + 1] = centres[at + 1] ?? 0;
    out[abeamAt + 2] = (centres[at + 2] ?? 0) + abeamZ * offset;
    const crossAt = (baseVertex + smokeVertexIndex(1, station, lane)) * 3;
    out[crossAt] = (centres[at] ?? 0) + (crossX / crossLength) * offset;
    out[crossAt + 1] = (centres[at + 1] ?? 0) + (crossY / crossLength) * offset;
    out[crossAt + 2] = (centres[at + 2] ?? 0) + (crossZ / crossLength) * offset;
  }
}
